import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {BackendService} from './core/services/backend.service';
import {LocalStorageService} from 'ngx-webstorage';
import {AppLocalStateService} from "./core/services/app-local-state.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {preferred_lang} from "./core/signals/translation.signal";
import { CookieService } from './core/services/cookie.service';
import { LanguageService } from './core/services/language.service';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';


@Component({
    selector: 'app-root',
    imports: [RouterOutlet, NgxTgModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit {
    title = 'FYES';

    @ViewChild("main") main: ElementRef | undefined;

    constructor(
        private backendService: BackendService,
        private localStorageService: LocalStorageService,
        public appLocal: AppLocalStateService,
        private cookieService: CookieService,
        private languageService: LanguageService,
        private zone: NgZone,
        private i18n: TranslateService,
    ) {
        // The signal defaults to 'en' — the persisted choice is the real answer.
        // Seed both it and the cookie the interceptor reads, otherwise a reload
        // renders Greek from localStorage while every request asks for `lang=en`.
        const lang = this.languageService.current;
        preferred_lang.set(lang);
        this.cookieService.setCookie('preferred_lang', lang, 365);

        // Add relevant user data to sentry for logging whenever they are updated
        this.appLocal.setupSentryUser();
        this.localStorageService.observe('installationId')
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.appLocal.setupSentryUser());
        this.localStorageService.observe('uid')
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.appLocal.setupSentryUser());
        this.localStorageService.observe('session_token')
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.appLocal.setupSentryUser());
    }

    /**
     * Dismisses the boot loader in index.html once the page is worth looking at.
     *
     * Waits for webfonts, for `load` (images included), and for the translations,
     * because those are what make the page assemble itself in front of the
     * reader. The strings matter most: the pipe paints '' synchronously and
     * fills in when the value lands, so without this the pills and buttons
     * surfaced empty and then filled, resizing as they went.
     *
     * start() is safe here even though the TG_INSTANCE factory already calls
     * run() — the runtime guards on a `running` flag and hands back the same
     * promise, so this awaits the load in flight rather than starting a second.
     *
     * Capped at 3s: one slow image below the fold must not hold the whole page
     * behind a loader. Run outside Angular — none of it should trigger change
     * detection.
     */
    ngAfterViewInit(): void {
        const boot = document.querySelector('.app-boot');
        if (!boot) return;

        const settled = Promise.all([
            (document as any).fonts?.ready ?? Promise.resolve(),
            document.readyState === 'complete'
                ? Promise.resolve()
                : new Promise<void>(r => window.addEventListener('load', () => r(), { once: true })),
            // resolves on failure too, so a dead CDN costs the cap, not a hang
            this.i18n.start().catch(() => undefined),
        ]);
        const capped = new Promise<void>(r => setTimeout(r, 3000));

        this.zone.runOutsideAngular(() => {
            Promise.race([settled, capped]).then(() => {
                // two frames, so the first real paint is on screen before the
                // cover starts fading rather than crossfading with it
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    boot.classList.add('is-done');
                    boot.addEventListener('transitionend', () => boot.remove(), { once: true });
                    // if the transition never fires (reduced motion, hidden tab)
                    setTimeout(() => boot.remove(), 600);
                }));
            });
        });
    }

    ngOnInit() {
        if (!this.localStorageService.retrieve('installationId')) {
            this.backendService.getInstallation().subscribe((response) => {
                if (response.installationId) {
                    this.localStorageService.store('installationId', response.installationId);
                }
            });
        }
    }
}
